// src/App.jsx
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Route, Routes, useParams, useLocation } from 'react-router-dom';
import { http } from './lib/http'; // ✅ use our fetch helper
import { LoadingProvider, useLoading } from './contexts/LoadingContext';
import { AuthProvider } from './contexts/AuthContext';
import LoadingOverlay from './components/LoadingOverlay';
import { useNavigationLoading } from './hooks/useNavigationLoading';
import ScrollToTopRouter from './components/ScrollToTopRouter';
import ForceScrollToTop from './components/ForceScrollToTop';
import ScrollToTopOnRouteChange from './components/ScrollToTopOnRouteChange';
import SplashScreen from './components/SplashScreen';

/* ─── chrome ─── */
import Navbar from './components/Navbar';
import { LanguageProvider } from './contexts/LanguageContext';
import Footer from './components/Footer';

/* ─── public pages ─── */
import Home     from './components/Home';
import Services from './components/Services';
import About    from './components/About';
import Team     from './components/Team';
import Blog     from './components/Blog';
import ForBusiness from './components/ForBusiness';
import FaqPage  from './components/FaqPage';
import Privacy  from './components/PrivacyPolicy';
import Support  from './components/Support';
import Contact  from './components/Contact';
import DeleteAccount from './components/DeleteAccount';

/* ─── auth ─── */
import Login          from './components/Login';
import Register       from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword  from './components/ResetPassword';
import ChangePassword from './components/ChangePassword';

/* ─── protected (client / owner) ─── */
import Explore            from './components/Explore';
import Dashboard          from './components/Dashboard';
import ShopOwnerDashboard from './components/ShopOwnerDashboard';
import ProfilePage        from './components/ProfilePage';
import Map                from './components/Map';

/* ─── misc ─── */
import BookingPage     from './components/BookingPage';
import ListUrBussiness from './components/ListUrBussiness';

/* ─── service categories ─── */
import Barber              from './components/Barber';
import HairNailSalon            from './components/HairNailSalon';
import TattooPiercingShop       from './components/TattooPiercingShop';
import HairRemoval              from './components/HairRemoval';
import MakeupBrowsLashes        from './components/BrowsLashes';
import MassageAestheticMedicine from './components/MassageAestheticMedicine';
import PetServices              from './components/PetServices';
import Makeup       from './components/Makeup';

/* ─── recommended (new) ─── */
import RecHairNailSalon             from './recommended/RecHairNailSalon';
import RecBarbershop                from './recommended/RecBarbershop';
import RecTattooPiercingShop        from './recommended/RecTattooPiercingShop';
import RecHairRemoval               from './recommended/RecHairRemoval';
import RecMakeupBrowsLashes         from './recommended/RecMakeupBrowsLashes';
import RecMassageAestheticMedicine  from './recommended/RecMassageAestheticMedicine';

/* ─── admin area ─── */
import AdminDashboard from './components/AdminDashboard';

/* ─── 404 ─── */
import NotFound from './components/NotFound';

/* ─── guard ─── */
import ProtectedRoute from './ProtectedRoute';


/* helper to wrap a page with navbar + footer */
const Page = ({ children }) => (
  <>
    <Navbar />
      {children}
    <Footer />
  </>
);


/* Wrapper for routes that need navigation loading */
const WithNavigationLoading = ({ children }) => {
  useNavigationLoading();
  return children;
};

/* 🔑 Prime CSRF once on app load (handles Netlify→Fly) */
function CsrfBoot() {
  useEffect(() => {
    http.ensureCsrf().catch(() => {});
  }, []);
  return null;
}

/* keep existing service mapping & route */
const SERVICE_MAP = {
  // New service mappings
  'barber': Barber,
  'hair-&-nail': HairNailSalon,
  'brows-&-lashes': MakeupBrowsLashes,
  'laser-clinics': HairRemoval,  // Using HairRemoval for Laser Clinics
  'tatoo-and-piercing': TattooPiercingShop,
  'makeup': Makeup,
  'massage': MassageAestheticMedicine,
  'pet-services': PetServices,
  // Legacy mappings for backward compatibility
  'barbershop': Barber,
  'hair-&-nail-salon': HairNailSalon,
  'tattoo-&-piercing-shops': TattooPiercingShop,
  'tattoo-&-piercing': TattooPiercingShop,   // alias
  'hair-removal': HairRemoval,
  'makeup-&-brows-&-lashes': MakeupBrowsLashes,
  'massage-&-aesthetic-medicine': MassageAestheticMedicine,
  'aesthetic-medicine': MassageAestheticMedicine, // alias
};


function ServicePage() {
  const { slug } = useParams();
  const Component = SERVICE_MAP[slug];
  if (!Component) {
    return (
      <Page>
        <NotFound />
      </Page>
    );
  }
  return <Page><Component /></Page>;
}

const REC_SERVICE_MAP = {
  'hair-&-nail-salon': RecHairNailSalon,
  'barbershop': RecBarbershop,
  'tattoo-&-piercing-shops': RecTattooPiercingShop,
  'tattoo-&-piercing': RecTattooPiercingShop,
  'hair-removal': RecHairRemoval,
  'brows-&-lashes': RecMakeupBrowsLashes,
  'makeup': RecMakeupBrowsLashes,            // ✅ added
  'makeup-&-brows-&-lashes': RecMakeupBrowsLashes,
  'massage': RecMassageAestheticMedicine,    // ✅ added
  'massage-&-aesthetic-medicine': RecMassageAestheticMedicine,
  'aesthetic-medicine': RecMassageAestheticMedicine,
};


function RecPage() {
  const { slug } = useParams();
  const Component = REC_SERVICE_MAP[slug];

  useLayoutEffect(() => {
    if (!Component) return undefined;
    document.body.classList.add('rec-route-page');
    return () => document.body.classList.remove('rec-route-page');
  }, [Component]);

  if (!Component) {
    return (
      <Page>
        <NotFound />
      </Page>
    );
  }
  return (
    <Page>
      <div className="rec-route">
        <Component />
      </div>
    </Page>
  );
}

/* Intercept all Link clicks to scroll before navigation */
const LinkClickInterceptor = () => {
  useEffect(() => {
    const handleClick = (e) => {
      // Find the closest Link or anchor tag
      const link = e.target.closest('a[href]');
      
      if (link) {
        const href = link.getAttribute('href');
        // Only intercept internal links (starting with /)
        if (href && href.startsWith('/') && !href.startsWith('//')) {
          // Scroll to top IMMEDIATELY when link is clicked, before navigation
          window.scrollTo(0, 0);
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        }
      }
    };
    
    // Use capture phase to catch events early, before React Router handles them
    document.addEventListener('click', handleClick, true);
    document.addEventListener('mousedown', handleClick, true);
    
    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('mousedown', handleClick, true);
    };
  }, []);
  
  return null;
};

/* Scroll prevention during navigation */
const ScrollPreventer = () => {
  const location = useLocation();
  
  useLayoutEffect(() => {
    // Lock scroll position during navigation
    const lockScroll = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    
    // Immediate lock
    lockScroll();
    
    // Prevent scroll events temporarily
    const preventScroll = (e) => {
      if (window.scrollY > 0 || document.documentElement.scrollTop > 0) {
        lockScroll();
      }
    };
    
    window.addEventListener('scroll', preventScroll, { passive: false, capture: true });
    
    // Release after a short delay
    const timeout = setTimeout(() => {
      window.removeEventListener('scroll', preventScroll, { capture: true });
    }, 500);
    
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', preventScroll, { capture: true });
    };
  }, [location.pathname]);
  
  return null;
};

/* Main app content with loading overlay */
const AppContent = () => {
  const { isLoading, loadingMessage } = useLoading();

  return (
    <>
      <CsrfBoot />
      <LinkClickInterceptor />
      <ScrollToTopOnRouteChange />
      <ForceScrollToTop />
      <ScrollPreventer />
      <ScrollToTopRouter>
        <Routes>
        {/* public landing - no navigation loading */}
        <Route path="/" element={<><Home /><Footer /></>} />

        {/* authentication — navbar + split + site footer */}
        <Route path="/login"    element={<WithNavigationLoading><Page><Login /></Page></WithNavigationLoading>} />
        <Route path="/register" element={<WithNavigationLoading><Page><Register /></Page></WithNavigationLoading>} />

        {/* keep the rest as-is; wrap others too if you want navbar/footer on them */}
        <Route path="/forgot-password" element={<WithNavigationLoading><Page><ForgotPassword /></Page></WithNavigationLoading>} />
        <Route path="/reset-password"  element={<WithNavigationLoading><Page><ResetPassword  /></Page></WithNavigationLoading>} />

        <Route path="/change-password" element={
          <WithNavigationLoading>
            <ProtectedRoute>
              <ChangePassword />
            </ProtectedRoute>
          </WithNavigationLoading>
        }/>

        {/* public content */}
        <Route path="/services" element={<WithNavigationLoading><Page><Services /></Page></WithNavigationLoading>} />
        <Route path="/about"    element={<WithNavigationLoading><Page><About    /></Page></WithNavigationLoading>} />
        <Route path="/for-business" element={<WithNavigationLoading><Page><ForBusiness /></Page></WithNavigationLoading>} />
        <Route path="/faq"      element={<WithNavigationLoading><Page><FaqPage  /></Page></WithNavigationLoading>} />
        <Route path="/team"     element={<WithNavigationLoading><Page><Team     /></Page></WithNavigationLoading>} />
        <Route path="/blog"     element={<WithNavigationLoading><Page><Blog     /></Page></WithNavigationLoading>} />
        <Route path="/privacy"  element={<WithNavigationLoading><Page><Privacy  /></Page></WithNavigationLoading>} />
        <Route path="/support"  element={<WithNavigationLoading><Page><Support  /></Page></WithNavigationLoading>} />
        <Route path="/contact"  element={<WithNavigationLoading><Page><Contact  /></Page></WithNavigationLoading>} />
        <Route path="/delete-account" element={<WithNavigationLoading><Page><DeleteAccount /></Page></WithNavigationLoading>} />

        {/* explore (client) */}
        <Route path="/explore" element={
          <ProtectedRoute role="client">
            <Explore />
          </ProtectedRoute>
        }/>

        {/* map (client) */}
        <Route path="/map" element={
          <ProtectedRoute role="client">
            <Map />
          </ProtectedRoute>
        }/>

        {/* profile (client) */}
        <Route path="/profile" element={
          <ProtectedRoute role="client">
            <Page><ProfilePage /></Page>
          </ProtectedRoute>
        }/>

        {/* service categories (dynamic) */}
        <Route path="/service/:slug" element={<WithNavigationLoading><ServicePage /></WithNavigationLoading>} />
        <Route path="/service/:slug/town/:town" element={<WithNavigationLoading><ServicePage /></WithNavigationLoading>} />

        {/* recommended (new) */}
        <Route path="/rec/:slug" element={<WithNavigationLoading><RecPage /></WithNavigationLoading>} />

        {/* booking / business */}
        <Route path="/booking/:shopName" element={<Page><BookingPage /></Page>} />
        <Route path="/ListUrBussiness"   element={<WithNavigationLoading><Page><ListUrBussiness /></Page></WithNavigationLoading>} />

        {/* client dashboards */}
        <Route path="/dashboard" element={
          <ProtectedRoute role="client">
            <Dashboard />
          </ProtectedRoute>
        }/>
        <Route path="/ShopOwnerDashboard" element={<WithNavigationLoading><ShopOwnerDashboard /></WithNavigationLoading>} />
        <Route path="/shopowner/*" element={<WithNavigationLoading><ShopOwnerDashboard /></WithNavigationLoading>} />
        <Route path="/ListUrBussiness"     element={<WithNavigationLoading><Page><ListUrBussiness /></Page></WithNavigationLoading>} />

        {/* admin area */}
        <Route path="/admin/*" element={<AdminDashboard />} />

        {/* 404 fallback */}
        <Route path="*" element={
          <WithNavigationLoading>
            <Page>
              <NotFound />
            </Page>
          </WithNavigationLoading>
        }/>
        </Routes>
      </ScrollToTopRouter>
      <LoadingOverlay isLoading={isLoading} message={loadingMessage} />
    </>
  );
};

export default function App() {
  const [showSplash, setShowSplash] = useState(true);

  const handleSplashComplete = () => {
    setShowSplash(false);
  };

  return (
    <LanguageProvider>
      {showSplash ? (
        <SplashScreen onComplete={handleSplashComplete} />
      ) : (
        <LoadingProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </LoadingProvider>
      )}
    </LanguageProvider>
  );
}
